import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "reflexbox"
    }}>{`Reflexbox`}</h1>
    <p>{`📦 Ergonomic, responsive React layout and grid system.
The original `}<em parentName="p">{`Box`}</em>{` component™ since 2015`}</p>
    <p><a parentName="p" {...{
        "href": "https://travis-ci.org/rebassjs/rebass"
      }}><img parentName="a" {...{
          "src": "https://flat.badgen.net/travis/rebassjs/rebass/master",
          "alt": "Build Status"
        }}></img></a>{`
`}<a parentName="p" {...{
        "href": "https://npmjs.com/package/reflexbox"
      }}><img parentName="a" {...{
          "src": "https://flat.badgen.net/npm/dw/reflexbox",
          "alt": "Downloads"
        }}></img></a>{`
`}<a parentName="p" {...{
        "href": "https://npmjs.com/package/reflexbox"
      }}><img parentName="a" {...{
          "src": "https://flat.badgen.net/npm/v/reflexbox",
          "alt": "Version"
        }}></img></a>{`
`}<a parentName="p" {...{
        "href": "LICENSE.md"
      }}><img parentName="a" {...{
          "src": "https://flat.badgen.net/badge/license/MIT/blue",
          "alt": "MIT License"
        }}></img></a>{`
`}<a parentName="p" {...{
        "href": "https://system-ui.com/theme"
      }}><img parentName="a" {...{
          "src": "https://flat.badgen.net/badge/system-ui/theme/black",
          "alt": "system-ui/theme"
        }}></img></a></p>
    <ul>
      <li parentName="ul">{`Primitive styled components for all your layout needs`}</li>
      <li parentName="ul">{`Customize styles inline with the `}<inlineCode parentName="li">{`sx`}</inlineCode>{` prop`}</li>
      <li parentName="ul">{`Ergonomic responsive array-based values`}</li>
      <li parentName="ul">{`Support for component variants`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://styled-system.com"
        }}>{`Styled System`}</a>{` props`}</li>
      <li parentName="ul">{`Themeable and compatible with the `}<a parentName="li" {...{
          "href": "https://github.com/system-ui/theme-specification"
        }}>{`Theme Specification`}</a></li>
      <li parentName="ul">{`Built with `}<a parentName="li" {...{
          "href": "https://styled-system.com"
        }}>{`Styled System`}</a></li>
      <li parentName="ul">{`Works with `}<a parentName="li" {...{
          "href": "https://theme-ui.com"
        }}>{`Theme UI`}</a></li>
      <li parentName="ul">{`Built with Emotion, with support for Styled Components`}</li>
    </ul>
    <h2 {...{
      "id": "getting-started"
    }}>{`Getting Started`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`npm i reflexbox
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import React from 'react'
import { Flex, Box } from 'reflexbox'

export default props =>
  <Flex flexWrap='wrap'>
    <Box
      width={[ 1, 1/2 ]}
      p={3}>
      Reflex
    </Box>
    <Box
      width={[ 1, 1/2 ]}
      p={3}>
      Box
    </Box>
  </Flex>
`}</code></pre>
    <h3 {...{
      "id": "sx-prop"
    }}><inlineCode parentName="h3">{`sx`}</inlineCode>{` Prop`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`Box`}</inlineCode>{` and `}<inlineCode parentName="p">{`Flex`}</inlineCode>{` components both accept a `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop that works with no additional setup required.
The `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop is similar to Emotion's `}<inlineCode parentName="p">{`css`}</inlineCode>{` prop, but allows you to use values derived from the theme object.`}</p>
    <p>{`Reflexbox follows the `}<a parentName="p" {...{
        "href": "https://github.com/system-ui/theme-specification"
      }}>{`Theme Specification`}</a>{`, which means that any theme created for use with `}<a parentName="p" {...{
        "href": "https://theme-ui.com"
      }}>{`Theme UI`}</a>{`, `}<a parentName="p" {...{
        "href": "https://styled-system.com"
      }}>{`Styled System`}</a>{`, or other similar libraries will work out-of-the-box.
This allows you to share design constraints for typography, color, and layout throughout your application using a theming context.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<Box
  sx={{
    p: 4,
    color: 'primary',
  }}
/>
`}</code></pre>
    <p>{`Note: to opt-out of theme-based styles, use the `}<inlineCode parentName="p">{`css`}</inlineCode>{` prop instead, which will not transform values.`}</p>
    <h2 {...{
      "id": "theming"
    }}>{`Theming`}</h2>
    <p>{`Because Reflexbox follows the `}<a parentName="p" {...{
        "href": "https://github.com/system-ui/theme-specification"
      }}>{`Theme Specification`}</a>{`, all themes built for use with `}<a parentName="p" {...{
        "href": "https://styled-system.com"
      }}>{`Styled System`}</a>{`, `}<a parentName="p" {...{
        "href": "https://theme-ui.com"
      }}>{`Theme UI`}</a>{`, or other related libraries are compatible with Reflexbox.`}</p>
    <p>{`To add a theme to an application, import the `}<inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{` component from `}<inlineCode parentName="p">{`emotion-theming`}</inlineCode>{` and pass a custom theme object in.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`npm i emotion-theming
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import React from 'react'
import { ThemeProvider } from 'emotion-theming'
import { Flex, Box } from 'reflexbox'

const theme = {
  breakpoints: [
    '40em', '52em', '64em',
  ],
  colors: {
    text: '#000',
    background: '#fff',
    primary: '#07c',
  },
  space: [
    0, 4, 8, 16, 32, 64, 128, 256,
  ],
}

export default props =>
  <ThemeProvider theme={theme}>
    <Box
      sx={{
        p: 4,
        bg: 'primary',
      }}>
      Hello
    </Box>
  </ThemeProvider>
`}</code></pre>
    <p>{`For use with `}<a parentName="p" {...{
        "href": "https://theme-ui.com"
      }}>{`Theme UI`}</a>{`, use `}<inlineCode parentName="p">{`gatsby-plugin-theme-ui`}</inlineCode>{` or import the Theme UI `}<inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{` instead.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { ThemeProvider } from 'theme-ui'
`}</code></pre>
    <h2 {...{
      "id": "variants"
    }}>{`Variants`}</h2>
    <p>{`Reflexbox components also accept a `}<inlineCode parentName="p">{`variant`}</inlineCode>{` prop, which allows you to define commonly used styles,
such as cards, badges, and CSS grid layouts, in your theme object for reuse.`}</p>
    <p>{`Add a `}<inlineCode parentName="p">{`variants`}</inlineCode>{` object to your theme and include any variants as style objects. These styles can reference other values in your theme such as colors, typographic styles, and more.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// example theme
export default {
  colors: {
    text: '#000',
    background: '#fff',
    primary: '#07c',
  },
  radii: {
    default: 4,
  },
  shadows: {
    card: '0 0 4px rgba(0, 0, 0, .125)',
  },
  variants: {
    card: {
      p: 3,
      borderRadius: 'default',
      bg: 'white',
      boxShadow: 'card',
    },
    badge: {
      color: 'white',
      bg: 'primary',
      p: 1,
      borderRadius: 'default',
    },
  },
}
`}</code></pre>
    <p>{`To apply a variant to your component, pass the name to the `}<inlineCode parentName="p">{`variant`}</inlineCode>{` prop.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<Box variant='card'>Card</Box>
<Box variant='badge'>Badge</Box>
`}</code></pre>
    <h2 {...{
      "id": "responsive-styles"
    }}>{`Responsive Styles`}</h2>
    <p>{`Use array values to quickly and ergonomically add mobile-first responsive styles to specific properties.
This works on all style props and the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop.
See the `}<a parentName="p" {...{
        "href": "https://styled-system.com"
      }}>{`Styled System`}</a>{` docs for more.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// 100% width at the smallest viewport width
// 50% width at the next breakpoint
// 25% width at the next breakpoint
<Box width={[ '100%', '50%', '25%' ]} />
`}</code></pre>
    <p>{`You can customize the widths used for each breakpoint by defining a `}<inlineCode parentName="p">{`theme.breakpoints`}</inlineCode>{` array in your theme.`}</p>
    <h2 {...{
      "id": "styled-system-props"
    }}>{`Styled System Props`}</h2>
    <p>{`Reflexbox conforms to the `}<a parentName="p" {...{
        "href": "https://github.com/system-ui/theme-specification"
      }}>{`Theme Specification`}</a>{` and includes many common `}<a parentName="p" {...{
        "href": "https://styled-system.com"
      }}>{`Styled System`}</a>{` props.
The `}<inlineCode parentName="p">{`Box`}</inlineCode>{` and `}<inlineCode parentName="p">{`Flex`}</inlineCode>{` components accept the following props:`}</p>
    <h3 {...{
      "id": "space-props"
    }}>{`Space Props`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Prop`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Theme Key`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`margin`}</inlineCode>{`, `}<inlineCode parentName="td">{`m`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`marginTop`}</inlineCode>{`, `}<inlineCode parentName="td">{`mt`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`marginRight`}</inlineCode>{`, `}<inlineCode parentName="td">{`mr`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`marginBottom`}</inlineCode>{`, `}<inlineCode parentName="td">{`mb`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`marginLeft`}</inlineCode>{`, `}<inlineCode parentName="td">{`ml`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`marginX`}</inlineCode>{`, `}<inlineCode parentName="td">{`mx`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`marginY`}</inlineCode>{`, `}<inlineCode parentName="td">{`my`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`padding`}</inlineCode>{`, `}<inlineCode parentName="td">{`p`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`paddingTop`}</inlineCode>{`, `}<inlineCode parentName="td">{`pt`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`paddingRight`}</inlineCode>{`, `}<inlineCode parentName="td">{`pr`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`paddingBottom`}</inlineCode>{`, `}<inlineCode parentName="td">{`pb`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`paddingLeft`}</inlineCode>{`, `}<inlineCode parentName="td">{`pl`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`paddingX`}</inlineCode>{`, `}<inlineCode parentName="td">{`px`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`paddingY`}</inlineCode>{`, `}<inlineCode parentName="td">{`py`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "layout-props"
    }}>{`Layout Props`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Prop`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Theme Key`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`width`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sizes`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`height`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sizes`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`minWidth`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sizes`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`maxWidth`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sizes`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`minHeight`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sizes`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`maxHeight`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sizes`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "typography-props"
    }}>{`Typography Props`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Prop`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Theme Key`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`fontFamily`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`fonts`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`fontSize`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`fontSizes`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`fontWeight`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`fontWeights`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`lineHeight`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`lineHeights`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`letterSpacing`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`letterSpacings`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`fontStyle`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`textAlign`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "color-props"
    }}>{`Color Props`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Prop`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Theme Key`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`color`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`colors`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`backgroundColor`}</inlineCode>{`, `}<inlineCode parentName="td">{`bg`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`colors`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`opacity`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "flexbox-props"
    }}>{`Flexbox Props`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Prop`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Theme Key`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`alignItems`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`alignContent`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`justifyItems`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`justifyContent`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`flexWrap`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`flexDirection`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`flex`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`flexGrow`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`flexShrink`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`flexBasis`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`justifySelf`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`alignSelf`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`order`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A`}</td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "styled-components"
    }}>{`Styled Components`}</h2>
    <p>{`To use Reflexbox with Styled Components, import components from `}<inlineCode parentName="p">{`reflexbox/styled-components`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { Flex, Box } from 'reflexbox/styled-components'
`}</code></pre>
    <h2 {...{
      "id": "about"
    }}>{`About`}</h2>
    <p>{`This library is the result of consolidating APIs and ergonomics from the original Reflexbox library, Grid Styled, and Rebass Grid.
Reflexbox originally appeared with the original version of Rebass in 2015.`}</p>
    <p><a parentName="p" {...{
        "href": "LICENSE.md"
      }}>{`MIT License`}</a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      